import { useState, useRef, ReactNode, useEffect, Fragment } from 'react';
import classNames from 'classnames';

import { Popover } from 'components-antd';
import { useOutsideClick } from 'hooks';
import { TooltipPlacement } from 'antd/es/tooltip';

import { Dots, Close } from 'components/Icons';
import { ICON_VARIANT_TYPE, Icons } from 'pages/FormProcess/Icons';

import styles from './styles.module.scss';
import { dynamicManager } from 'pages/FormProcess/DynamicForm/DynamicManager';
import { DeclineFormModal } from '../../../DeclineButton/DeclineFormModal';
import { useDownloadPDF } from 'pages/Workshop/Forms/components/FormDetailModal/components/DownloadFilesModal/hooks/useDownloadPDF';
import { Spinner } from 'components';

interface SignViewOptionsPopoverType {
  disabled?: boolean;
  showDecline?: boolean;
  setDisabled?: () => void;
  onCloseForm: () => void;
}

export const SignViewOptionsPopover = ({
  disabled = false,
  showDecline,
  onCloseForm,
}: SignViewOptionsPopoverType) => {
  const [open, setOpen] = useState(false);
  const [openDecline, setDecline] = useState(false);

  const buttonRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLInputElement>(null);

  const { downloading, downloadCombinedFile } = useDownloadPDF({
    anonymousUserDetails: dynamicManager.getAnonymousDetails(),
    isCompleted: false,
    downloadedFileName: dynamicManager.getFormName(),
    formProcessPublicId: dynamicManager.getFormProcessPublicId(),
  });

  useOutsideClick([buttonRef, optionsRef], () => setOpen(false));

  const onClickOptions = (e) => {
    if (disabled) return;

    e.stopPropagation();
    setOpen(!open);
  };

  const options = [
    {
      label: 'Finish Later',
      icon: <Icons variant={ICON_VARIANT_TYPE.FINISH_SIGNING} />,
      onClick: () => onCloseForm(),
    },
    ...(showDecline
      ? [
          {
            label: 'Decline to Sign',
            icon: <Close />,
            onClick: () => setDecline(true),
          },
        ]
      : []),
    {
      label: 'Print & Sign',
      icon: <Icons variant={ICON_VARIANT_TYPE.Signature} />,
      disableCloseOnClick: true,
      loading: downloading,
      onClick: () =>
        downloadCombinedFile(
          dynamicManager.getFormProcessPublicId(),
          dynamicManager.getDynamicDocuments(),
        ),
    },
  ];

  const PopoverContent = () => (
    <div ref={optionsRef} className={classNames(styles.options)}>
      <ul>
        {options.map(({ label, icon, onClick, loading, disableCloseOnClick }, idx) => (
          <>
            <li
              key={idx}
              className={classNames(styles.item)}
              onClick={() => {
                if (!disableCloseOnClick) {
                  setOpen(false);
                }
                onClick();
              }}
            >
              {loading ? (
                <Spinner loaderClassName={styles.optionLoader} />
              ) : (
                <Fragment>
                  <div className={styles.icon}>{icon}</div>
                  <span>{label}</span>
                </Fragment>
              )}
            </li>
          </>
        ))}
      </ul>
    </div>
  );

  return (
    <Fragment>
      <Popover
        open={open}
        placement={'topLeft'}
        overlayClassName={classNames(styles.signOptionsPopover)}
        content={<PopoverContent />}
        getPopupContainer={(triggerNode) => triggerNode}
      >
        <div
          className={classNames(styles.signOptionsWrapper, { [styles.openedOptionsWrapper]: open })}
          testid="options"
          ref={buttonRef}
          onClick={onClickOptions}
        >
          <span className={classNames(styles.title)}>
            <Dots className={styles.optionsIcon} />
          </span>
        </div>
      </Popover>

      <DeclineFormModal open={openDecline} onClose={() => setDecline(false)} />
    </Fragment>
  );
};
